import React, {useState, useEffect} from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import './Profile.css';

const API_URL = import.meta.env.VITE_API_URL;

const Profile = ({onLogout, isAdmin = false}) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState('profile'); // profile, demandes, reclamations
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showDemandeForm, setShowDemandeForm] = useState(false);
  const [showReclamationForm, setShowReclamationForm] = useState(false);
  const [showDetailDevis, setShowDetailDevis] = useState(false);
  const [selectedDemande, setSelectedDemande] = useState(null);
  const [uploadingContract, setUploadingContract] = useState(false);

  // États pour demandes et réclamations
  const [demandes, setDemandes] = useState([]);
  const [reclamations, setReclamations] = useState([]);
  const [factures, setFactures] = useState([]);
  const [loadingDemandes, setLoadingDemandes] = useState(false);
  const [loadingReclamations, setLoadingReclamations] = useState(false);
  const [loadingFactures, setLoadingFactures] = useState(false);

  const [editForm, setEditForm] = useState({
    nom: '',
    prenom: '',
    age: '',
    numeroTelephone: '',
    adresse: '',
    region: '',
    ville: ''
  });

  const [passwordForm, setPasswordForm] = useState({
    ancienMotDePasse: '',
    nouveauMotDePasse: '',
    confirmerMotDePasse: ''
  });

  const [demandeForm, setDemandeForm] = useState({
    consommationMensuelle: '',
    periodeConsommation: 'mensuelle',
    pompage: false,
    consommationPompage: '',
    avisPhotovoltaique: '',
    perspective: '',
    modePaiement: 'credit'
  });

  const [calculResult, setCalculResult] = useState(null);

  const [reclamationForm, setReclamationForm] = useState({
    sujet: '',
    message: ''
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(() => {
    if (activeTab === 'demandes') {
      fetchDemandes();
    } else if (activeTab === 'reclamations') {
      fetchReclamations();
    } else if (activeTab === 'factures') {
      fetchFactures();
    }
  }, [activeTab]);

  const fetchProfile = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setMessage('Non authentifié');
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API_URL}/auth/profile`, {
        headers: {Authorization: `Bearer ${token}`}
      });
      setUser(res.data);
      setEditForm({
        nom: res.data.nom || '',
        prenom: res.data.prenom || '',
        age: res.data.age || '',
        numeroTelephone: res.data.numeroTelephone || '',
        adresse: res.data.adresse || '',
        region: res.data.region || '',
        ville: res.data.ville || ''
      });
    } catch (err) {
      setMessage('Erreur lors du chargement du profil');
    } finally {
      setLoading(false);
    }
  };

  const fetchDemandes = async () => {
    setLoadingDemandes(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/demandes-devis/mes-demandes`, {
        headers: {Authorization: `Bearer ${token}`}
      });
      setDemandes(response.data);
    } catch (error) {
      console.error('Erreur récupération demandes:', error);
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: 'Impossible de charger vos demandes de devis'
      });
    } finally {
      setLoadingDemandes(false);
    }
  };

  const fetchReclamations = async () => {
    setLoadingReclamations(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/reclamations/mes-reclamations`, {
        headers: {Authorization: `Bearer ${token}`}
      });
      setReclamations(response.data);
    } catch (error) {
      console.error('Erreur récupération réclamations:', error);
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: 'Impossible de charger vos réclamations'
      });
    } finally {
      setLoadingReclamations(false);
    }
  };

  const fetchFactures = async () => {
    setLoadingFactures(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/documents-financiers/mes-factures`, {
        headers: {Authorization: `Bearer ${token}`}
      });
      setFactures(response.data.factures);
    } catch (error) {
      console.error('Erreur récupération factures:', error);
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: 'Impossible de charger vos factures'
      });
    } finally {
      setLoadingFactures(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    if (onLogout) onLogout();
  };

  const handleEditProfile = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(`${API_URL}/auth/update-profile`, editForm, {
        headers: {Authorization: `Bearer ${token}`}
      });
      setMessage('Profil mis à jour avec succès');
      setUser(res.data.user);
      setShowEditProfile(false);
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage(err.response?.data?.message || 'Erreur lors de la mise à jour');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.nouveauMotDePasse !== passwordForm.confirmerMotDePasse) {
      setMessage('Les mots de passe ne correspondent pas');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/auth/update-password`, {
        ancienMotDePasse: passwordForm.ancienMotDePasse,
        nouveauMotDePasse: passwordForm.nouveauMotDePasse
      }, {
        headers: {Authorization: `Bearer ${token}`}
      });
      setMessage('Mot de passe mis à jour avec succès');
      setShowChangePassword(false);
      setPasswordForm({ancienMotDePasse: '', nouveauMotDePasse: '', confirmerMotDePasse: ''});
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage(err.response?.data?.message || 'Erreur lors de la mise à jour');
    }
  };

  // Fonction pour calculer les résultats d'estimation
  const calculateResults = (consommationMensuelle, periodeConsommation, modePaiement) => {
    if (!consommationMensuelle || consommationMensuelle <= 0) {
      setCalculResult(null);
      return;
    }

    // Calcul de la consommation annuelle selon la période
    let consommationAnnuelle;
    switch (periodeConsommation) {
      case 'mensuelle':
        consommationAnnuelle = parseFloat(consommationMensuelle) * 12;
        break;
      case 'trimestrielle':
        consommationAnnuelle = parseFloat(consommationMensuelle) * 4;
        break;
      case 'annuelle':
        consommationAnnuelle = parseFloat(consommationMensuelle);
        break;
      default:
        consommationAnnuelle = parseFloat(consommationMensuelle) * 12;
    }

    // Calcul de la puissance nécessaire
    const puissanceNecessaire = consommationAnnuelle / 1600;

    // Arrondir à la valeur commerciale la plus proche
    const valeursCommerciales = [1, 2, 3, 5, 7, 10, 15, 20, 25, 30, 40, 50];
    let puissanceCommerciale = valeursCommerciales[0];
    for (let i = 0; i < valeursCommerciales.length; i++) {
      if (Math.abs(valeursCommerciales[i] - puissanceNecessaire) < Math.abs(puissanceCommerciale - puissanceNecessaire)) {
        puissanceCommerciale = valeursCommerciales[i];
      }
    }

    // Puissance minimale de 2 kWc
    if (puissanceCommerciale < 2) {
      puissanceCommerciale = 2;
    }

    // Logique spécifique pour les puissances entre 2 et 4
    if (puissanceNecessaire > 2 && puissanceNecessaire <= 3) {
      puissanceCommerciale = 3;
    } else if (puissanceNecessaire > 3 && puissanceNecessaire <= 4) {
      puissanceCommerciale = 4;
    }

    // Vérifier si la puissance dépasse 4 kWc (installation industrielle)
    const isIndustrielle = puissanceNecessaire > 4;

    // Déterminer le type de formulaire et le prix
    let typeFormulaire;
    let prixFormulaire;
    let typeContrat;

    if (modePaiement === 'comptant') {
      typeFormulaire = 'Non applicable';
      prixFormulaire = 0;
      typeContrat = 'Contrat hors programme Prosol électrique';
    } else {
      if (puissanceCommerciale <= 2) {
        typeFormulaire = 'Formulaire 7500 DT';
        prixFormulaire = 7500;
      } else {
        typeFormulaire = 'Formulaire 10000 DT';
        prixFormulaire = 10000;
      }
      typeContrat = 'Contrat crédit + Formulaire Prosol électrique obligatoire';
    }

    setCalculResult({
      consommationAnnuelle: Math.round(consommationAnnuelle * 100) / 100,
      puissanceNecessaire: Math.round(puissanceNecessaire * 100) / 100,
      puissanceCommerciale,
      typeFormulaire,
      prixFormulaire,
      typeContrat,
      modePaiement,
      isIndustrielle
    });
  };

  const handleSubmitDemande = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/demandes-devis`,
        {
          consommationMensuelle: parseFloat(demandeForm.consommationMensuelle),
          periodeConsommation: demandeForm.periodeConsommation,
          pompage: demandeForm.pompage,
          consommationPompage: demandeForm.pompage ? parseFloat(demandeForm.consommationPompage) : 0,
          avisPhotovoltaique: demandeForm.avisPhotovoltaique,
          perspective: demandeForm.perspective,
          modePaiement: demandeForm.modePaiement
        },
        {headers: {Authorization: `Bearer ${token}`}}
      );
      Swal.fire({
        icon: 'success',
        title: 'Demande envoyée',
        text: 'Votre demande de devis a été envoyée avec succès.'
      });
      setShowDemandeForm(false);
      setDemandeForm({
        consommationMensuelle: '',
        periodeConsommation: 'mensuelle',
        pompage: false,
        consommationPompage: '',
        avisPhotovoltaique: '',
        perspective: '',
        modePaiement: 'credit'
      });
      setCalculResult(null);
      fetchDemandes();
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: error.response?.data?.message || 'Erreur lors de l\'envoi de la demande'
      });
    }
  };

  const handleSubmitReclamation = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/reclamations`,
        reclamationForm,
        {headers: {Authorization: `Bearer ${token}`}}
      );
      Swal.fire({
        icon: 'success',
        title: 'Réclamation envoyée',
        text: 'Votre réclamation a été envoyée avec succès.'
      });
      setShowReclamationForm(false);
      setReclamationForm({sujet: '', message: ''});
      fetchReclamations();
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: error.response?.data?.message || 'Erreur lors de l\'envoi de la réclamation'
      });
    }
  };

  const handleOpenDetailDevis = (demande) => {
    setSelectedDemande(demande);
    setShowDetailDevis(true);
  };

  const handleUploadContract = async (e) => {
    const files = Array.from(e.target.files);

    if (!files || files.length === 0) return;

    const nombreContratsActuels = selectedDemande?.contratsClient?.length || 0;
    const nombreContratsRequis = selectedDemande?.nombreContratsRequis || 1;

    if (nombreContratsActuels + files.length > nombreContratsRequis) {
      Swal.fire({
        icon: 'warning',
        title: 'Nombre de contrats dépassé',
        text: `Vous ne pouvez téléverser que ${nombreContratsRequis} contrat(s). Vous avez déjà ${nombreContratsActuels} contrat(s).`
      });
      return;
    }

    // Vérifier que ce sont des PDF
    const invalidFiles = files.filter(file => file.type !== 'application/pdf');
    if (invalidFiles.length > 0) {
      Swal.fire({
        icon: 'error',
        title: 'Format invalide',
        text: 'Seuls les fichiers PDF sont acceptés.'
      });
      return;
    }

    setUploadingContract(true);

    try {
      const token = localStorage.getItem('token');

      for (const file of files) {
        const formData = new FormData();
        formData.append('contrat', file);

        await axios.post(
          `${API_URL}/demandes-devis/${selectedDemande._id}/upload-contrat`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
      }

      Swal.fire({
        icon: 'success',
        title: 'Contrat(s) téléversé(s)',
        text: 'Vos contrats ont été téléversés avec succès.'
      });

      // Rafraîchir les demandes
      await fetchDemandes();

      // Mettre à jour la demande sélectionnée
      const updatedDemandes = await axios.get(`${API_URL}/demandes-devis/mes-demandes`, {
        headers: {Authorization: `Bearer ${token}`}
      });
      const updatedDemande = updatedDemandes.data.find(d => d._id === selectedDemande._id);
      setSelectedDemande(updatedDemande);

      e.target.value = null;
    } catch (error) {
      console.error('Erreur upload contrat:', error);
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: error.response?.data?.message || 'Erreur lors du téléversement du contrat.'
      });
    } finally {
      setUploadingContract(false);
    }
  };

  const handleDeleteContract = async (contractId) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Supprimer ce contrat ?',
      text: 'Cette action est irréversible.',
      showCancelButton: true,
      confirmButtonText: 'Supprimer',
      cancelButtonText: 'Annuler',
      confirmButtonColor: '#f56565'
    });

    if (!result.isConfirmed) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_URL}/demandes-devis/${selectedDemande._id}/contrat/${contractId}`,
        {headers: {Authorization: `Bearer ${token}`}}
      );

      Swal.fire({
        icon: 'success',
        title: 'Contrat supprimé',
        text: 'Le contrat a été supprimé avec succès.'
      });

      // Rafraîchir les demandes
      await fetchDemandes();

      // Mettre à jour la demande sélectionnée
      const updatedDemandes = await axios.get(`${API_URL}/demandes-devis/mes-demandes`, {
        headers: {Authorization: `Bearer ${token}`}
      });
      const updatedDemande = updatedDemandes.data.find(d => d._id === selectedDemande._id);
      setSelectedDemande(updatedDemande);
    } catch (error) {
      console.error('Erreur suppression contrat:', error);
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: error.response?.data?.message || 'Erreur lors de la suppression du contrat.'
      });
    }
  };

  const getStatutBadgeClass = (statut) => {
    switch (statut) {
      case 'validé':
      case 'résolu':
        return 'badge-success';
      case 'rejeté':
        return 'badge-danger';
      case 'en_cours':
        return 'badge-info';
      default:
        return 'badge-warning';
    }
  };

  const getStatutLabel = (statut) => {
    switch (statut) {
      case 'en_attente':
        return 'En attente';
      case 'validé':
        return 'Validé';
      case 'rejeté':
        return 'Rejeté';
      case 'en_cours':
        return 'En cours';
      case 'résolu':
        return 'Résolu';
      default:
        return statut;
    }
  };

  if (loading) return (
    <div className="profile-loading">
      <LoadingSpinner message="Chargement du profil..." />
    </div>
  );

  if (message && !user) return (
    <div className="profile-container">
      <div className="error-message">{message}</div>
    </div>
  );

  return (
    <div className="profile-page">
      <div className="profile-header">
        <div className="profile-avatar">
          <div className="avatar-circle">
            {user?.prenom?.charAt(0)}{user?.nom?.charAt(0)}
          </div>
        </div>
        <div className="profile-title">
          <h1>{user?.prenom} {user?.nom}</h1>
          <p>{user?.mail}</p>
        </div>
      </div>

      {message && (
        <div className={`message ${
          message.toLowerCase().includes('succès') || 
          message.toLowerCase().includes('réussie') || 
          message.toLowerCase().includes('valide') ||
          message.toLowerCase().includes('reçu')
          ? 'success' : 'error'}`}>
          {message}
        </div>
      )}

      <div className={`profile-layout ${isAdmin ? 'admin-layout' : ''}`}>
        {/* Sidebar */}
        {!isAdmin && (
          <div className="profile-sidebar">
            <button
              className={`sidebar-item ${activeTab === 'profile' ? 'active' : ''}`}
              onClick={() => setActiveTab('profile')}
            >
              <span className="icon">👤</span>
              <span>Mon Profil</span>
            </button>
            <button
              className={`sidebar-item ${activeTab === 'demandes' ? 'active' : ''}`}
              onClick={() => setActiveTab('demandes')}
            >
              <span className="icon">📋</span>
              <span>Mes Demandes de Devis</span>
            </button>
            <button
              className={`sidebar-item ${activeTab === 'reclamations' ? 'active' : ''}`}
              onClick={() => setActiveTab('reclamations')}
            >
              <span className="icon">📢</span>
              <span>Mes Réclamations</span>
            </button>
            <button
              className={`sidebar-item ${activeTab === 'factures' ? 'active' : ''}`}
              onClick={() => setActiveTab('factures')}
            >
              <span className="icon">🧾</span>
              <span>Mes Factures</span>
            </button>
            <button className="sidebar-item logout-item" onClick={handleLogout}>
              <span className="icon">🚪</span>
              <span>Se déconnecter</span>
            </button>
          </div>
        )}

        {/* Main Content */}
        <div className="profile-content">
          {activeTab === 'profile' && (
            <>
              <div className="profile-section">
                <div className="profile-section-header">
                  <h2>Informations Personnelles</h2>
                  <button
                    className="btn-profile-edit"
                    onClick={() => setShowEditProfile(true)}
                  >
                    ✏️ Modifier Profil
                  </button>
                </div>
                <div className="info-grid">
                  <div className="info-card">
                    <label>Nom</label>
                    <p>{user?.nom || 'Non spécifié'}</p>
                  </div>
                  <div className="info-card">
                    <label>Prénom</label>
                    <p>{user?.prenom || 'Non spécifié'}</p>
                  </div>
                  <div className="info-card">
                    <label>Email</label>
                    <p>{user?.mail}</p>
                  </div>
                  <div className="info-card">
                    <label>Âge</label>
                    <p>{user?.age || 'Non spécifié'}</p>
                  </div>
                  <div className="info-card">
                    <label>Téléphone</label>
                    <p>{user?.numeroTelephone || 'Non spécifié'}</p>
                  </div>
                  <div className="info-card">
                    <label>Adresse</label>
                    <p>{user?.adresse || 'Non spécifié'}</p>
                  </div>
                  <div className="info-card">
                    <label>Région</label>
                    <p>{user?.region || 'Non spécifié'}</p>
                  </div>
                  <div className="info-card">
                    <label>Ville</label>
                    <p>{user?.ville || 'Non spécifié'}</p>
                  </div>
                </div>
              </div>

              <div className="profile-section">
                <div className="profile-section-header">
                  <h2>Sécurité</h2>
                  <button
                    className="btn-profile-security"
                    onClick={() => setShowChangePassword(true)}
                  >
                    🔒 Changer Mot de Passe
                  </button>
                </div>
                <div className="security-info">
                  <p>Dernière modification du mot de passe : Jamais</p>
                </div>
              </div>
            </>
          )}

          {activeTab === 'demandes' && (
            <div className="demandes-section">
              <div className="profile-section-header">
                <h2>Mes Demandes de Devis</h2>
                <button
                  className="btn-primary"
                  onClick={() => setShowDemandeForm(true)}
                  style={{
                    background: '#1e3a5f',
                    color: '#ffffff',
                    border: '2px solid #1e3a5f',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    fontWeight: '600'
                  }}
                >
                  + Nouvelle Demande
                </button>
              </div>

              {loadingDemandes ? (
                <LoadingSpinner message="Chargement des demandes..." />
              ) : demandes.length === 0 ? (
                <div className="empty-state">
                  <p>Vous n'avez pas encore de demande de devis.</p>
                  <button
                    className="btn-primary"
                    onClick={() => setShowDemandeForm(true)}
                    style={{
                      background: '#1e3a5f',
                      color: '#ffffff',
                      border: '2px solid #1e3a5f',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      fontWeight: '600'
                    }}
                  >
                    Faire une demande
                  </button>
                </div>
              ) : (
                <div className="demandes-list">
                  {demandes.map((demande) => (
                    <div key={demande._id} className="demande-card">
                      <div className="card-header">
                        <span className="date">
                          {new Date(demande.createdAt).toLocaleDateString('fr-FR')}
                        </span>
                        <span className={`badge ${getStatutBadgeClass(demande.statut)}`}>
                          {getStatutLabel(demande.statut)}
                        </span>
                      </div>
                      <div className="card-body">
                        <p><strong>Consommation annuelle:</strong> {demande.consommationAnnuelle} kWh/an</p>
                        <p><strong>Puissance recommandée:</strong> {demande.puissanceCommerciale} kWc</p>
                        <p><strong>Mode de paiement:</strong> {demande.modePaiement === 'comptant' ? 'Comptant' : 'Crédit'}</p>
                        {demande.modePaiement === 'credit' && (
                          <p><strong>Formulaire Prosol:</strong> {demande.typeFormulaireProsol}</p>
                        )}
                        <p><strong>Contrats déposés:</strong> {demande.contratsClient?.length || 0} / {demande.nombreContratsRequis || 1}</p>
                        {demande.pompage && (
                          <p><strong>Consommation pompage:</strong> {demande.consommationPompage} kWh</p>
                        )}
                        {demande.commentaireAdmin && (
                          <div className="admin-comment">
                            <strong>Commentaire admin:</strong>
                            <p>{demande.commentaireAdmin}</p>
                          </div>
                        )}
                      </div>
                      <div className="card-footer">
                        <button
                          className="btn-detail-devis"
                          onClick={() => handleOpenDetailDevis(demande)}
                          style={{
                            background: '#1e3a5f',
                            color: 'white',
                            border: 'none',
                            padding: '10px 20px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            fontSize: '0.95rem'
                          }}
                        >
                          📄 Détail devis & Upload contrats
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'reclamations' && (
            <div className="reclamations-section">
              <div className="profile-section-header">
                <h2>Mes Réclamations</h2>
                <button
                  className="btn-primary"
                  onClick={() => setShowReclamationForm(true)}
                  style={{
                    background: '#1e3a5f',
                    color: '#ffffff',
                    border: '2px solid #1e3a5f',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    fontWeight: '600'
                  }}
                >
                  + Nouvelle Réclamation
                </button>
              </div>

              {loadingReclamations ? (
                <LoadingSpinner message="Chargement des réclamations..." />
              ) : reclamations.length === 0 ? (
                <div className="empty-state">
                  <p>Vous n'avez pas encore de réclamation.</p>
                  <button
                    className="btn-primary"
                    onClick={() => setShowReclamationForm(true)}
                    style={{
                      background: '#1e3a5f',
                      color: '#ffffff',
                      border: '2px solid #1e3a5f',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      fontWeight: '600'
                    }}
                  >
                    Déposer une réclamation
                  </button>
                </div>
              ) : (
                <div className="reclamations-list">
                  {reclamations.map((reclamation) => (
                    <div key={reclamation._id} className="reclamation-card">
                      <div className="card-header">
                        <span className="date">
                          {new Date(reclamation.createdAt).toLocaleDateString('fr-FR')}
                        </span>
                        <span className={`badge ${getStatutBadgeClass(reclamation.statut)}`}>
                          {getStatutLabel(reclamation.statut)}
                        </span>
                      </div>
                      <div className="card-body">
                        <h4>{reclamation.sujet}</h4>
                        <p>{reclamation.message}</p>
                        {reclamation.reponseAdmin && (
                          <div className="admin-response">
                            <strong>Réponse de l'administrateur:</strong>
                            <p>{reclamation.reponseAdmin}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'factures' && (
            <div className="factures-section">
              <div className="profile-section-header">
                <h2>Mes Factures</h2>
              </div>

              {loadingFactures ? (
                <div className="loading-spinner">Chargement...</div>
              ) : factures.length === 0 ? (
                <div className="empty-state">
                  <p>Vous n'avez pas encore de facture validée.</p>
                </div>
              ) : (
                <div className="factures-list">
                  {factures.map((facture) => (
                    <div key={facture._id} className="facture-card">
                      <div className="facture-header">
                        <div className="facture-icon">🧾</div>
                        <div className="facture-info">
                          <h3>{facture.numero}</h3>
                          <span className="facture-date">
                            {new Date(facture.dateCreation).toLocaleDateString('fr-FR')}
                          </span>
                        </div>
                        <div className="facture-montant">
                          <span className="montant-label">Montant TTC</span>
                          <span className="montant-value">
                            {(facture.montantTTC || facture.montant).toFixed(2)} TND
                          </span>
                        </div>
                      </div>

                      {facture.description && (
                        <div className="facture-description">
                          <p>{facture.description}</p>
                        </div>
                      )}

                      <div className="facture-details">
                        <div className="detail-item">
                          <span className="label">Montant HT:</span>
                          <span className="value">{facture.montantHT?.toFixed(2)} TND</span>
                        </div>
                        <div className="detail-item">
                          <span className="label">TVA:</span>
                          <span className="value">{facture.tva?.toFixed(2)} TND</span>
                        </div>
                        <div className="detail-item">
                          <span className="label">Validée le:</span>
                          <span className="value">
                            {new Date(facture.dateValidation).toLocaleDateString('fr-FR')}
                          </span>
                        </div>
                      </div>

                      <div className="facture-actions">
                        <button
                          className="btn-download"
                          onClick={() => {
                            const token = localStorage.getItem('token');
                            window.open(`${API_URL}/documents-financiers/${facture._id}/pdf?token=${token}`, '_blank');
                          }}
                        >
                          📥 Télécharger le PDF
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal Modifier Profil */}
      {showEditProfile && (
        <div className="modal-overlay" onClick={() => setShowEditProfile(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Modifier le Profil</h3>
              <button className="close-btn" onClick={() => setShowEditProfile(false)}>×</button>
            </div>
            <form onSubmit={handleEditProfile} className="edit-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Nom</label>
                  <input
                    type="text"
                    value={editForm.nom}
                    onChange={(e) => setEditForm({...editForm, nom: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Prénom</label>
                  <input
                    type="text"
                    value={editForm.prenom}
                    onChange={(e) => setEditForm({...editForm, prenom: e.target.value})}
                    required
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Âge</label>
                  <input
                    type="number"
                    value={editForm.age}
                    onChange={(e) => setEditForm({...editForm, age: e.target.value})}
                    min="1"
                    max="120"
                  />
                </div>
                <div className="form-group">
                  <label>Téléphone</label>
                  <input
                    type="tel"
                    value={editForm.numeroTelephone}
                    onChange={(e) => setEditForm({...editForm, numeroTelephone: e.target.value})}
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Adresse</label>
                <input
                  type="text"
                  value={editForm.adresse}
                  onChange={(e) => setEditForm({...editForm, adresse: e.target.value})}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Région</label>
                  <input
                    type="text"
                    value={editForm.region}
                    onChange={(e) => setEditForm({...editForm, region: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Ville</label>
                  <input
                    type="text"
                    value={editForm.ville}
                    onChange={(e) => setEditForm({...editForm, ville: e.target.value})}
                  />
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="cancel-btn" onClick={() => setShowEditProfile(false)}>
                  Annuler
                </button>
                <button type="submit" className="save-btn">
                  Enregistrer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Changer Mot de Passe */}
      {showChangePassword && (
        <div className="modal-overlay" onClick={() => setShowChangePassword(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Changer le Mot de Passe</h3>
              <button className="close-btn" onClick={() => setShowChangePassword(false)}>×</button>
            </div>
            <form onSubmit={handleChangePassword} className="edit-form">
              <div className="form-group">
                <label>Ancien mot de passe</label>
                <input
                  type="password"
                  value={passwordForm.ancienMotDePasse}
                  onChange={(e) => setPasswordForm({...passwordForm, ancienMotDePasse: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Nouveau mot de passe</label>
                <input
                  type="password"
                  value={passwordForm.nouveauMotDePasse}
                  onChange={(e) => setPasswordForm({...passwordForm, nouveauMotDePasse: e.target.value})}
                  required
                  minLength="6"
                />
              </div>
              <div className="form-group">
                <label>Confirmer le nouveau mot de passe</label>
                <input
                  type="password"
                  value={passwordForm.confirmerMotDePasse}
                  onChange={(e) => setPasswordForm({...passwordForm, confirmerMotDePasse: e.target.value})}
                  required
                  minLength="6"
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="cancel-btn" onClick={() => setShowChangePassword(false)}>
                  Annuler
                </button>
                <button type="submit" className="save-btn">
                  Changer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Nouvelle Demande de Devis */}
      {showDemandeForm && (
        <div className="modal-overlay" onClick={() => setShowDemandeForm(false)}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Nouvelle Demande de Devis</h3>
              <button className="close-btn" onClick={() => setShowDemandeForm(false)}>×</button>
            </div>
            <form onSubmit={handleSubmitDemande} className="edit-form">
              <div className="form-section">
                <h4 style={{marginBottom: '20px', color: '#1e3a5f'}}>📊 Informations sur votre consommation</h4>

                <div className="form-group">
                  <label>Période de consommation *</label>
                  <select
                    name="periodeConsommation"
                    value={demandeForm.periodeConsommation}
                    onChange={(e) => {
                      const newFormData = {...demandeForm, periodeConsommation: e.target.value};
                      setDemandeForm(newFormData);
                      calculateResults(newFormData.consommationMensuelle, e.target.value, newFormData.modePaiement);
                    }}
                    required
                    className="form-select"
                  >
                    <option value="mensuelle">Mensuelle (par mois)</option>
                    <option value="trimestrielle">Trimestrielle (par 3 mois)</option>
                    <option value="annuelle">Annuelle (par an)</option>
                  </select>
                  <small>Choisissez la période correspondant à votre facture</small>
                </div>

                <div className="form-group">
                  <label>
                    Consommation {demandeForm.periodeConsommation === 'mensuelle' ? 'mensuelle' : demandeForm.periodeConsommation === 'trimestrielle' ? 'trimestrielle' : 'annuelle'} (kWh) *
                  </label>
                  <input
                    type="number"
                    value={demandeForm.consommationMensuelle}
                    onChange={(e) => {
                      const newFormData = {...demandeForm, consommationMensuelle: e.target.value};
                      setDemandeForm(newFormData);
                      calculateResults(e.target.value, newFormData.periodeConsommation, newFormData.modePaiement);
                    }}
                    required
                    min="0"
                    step="0.01"
                    placeholder={demandeForm.periodeConsommation === 'mensuelle' ? 'Ex: 350' : demandeForm.periodeConsommation === 'trimestrielle' ? 'Ex: 1050' : 'Ex: 4200'}
                  />
                  <small>Vous pouvez trouver cette information sur votre facture d'électricité</small>
                </div>

                <div className="form-group">
                  <label>Mode de paiement *</label>
                  <select
                    name="modePaiement"
                    value={demandeForm.modePaiement}
                    onChange={(e) => {
                      const newFormData = {...demandeForm, modePaiement: e.target.value};
                      setDemandeForm(newFormData);
                      calculateResults(newFormData.consommationMensuelle, newFormData.periodeConsommation, e.target.value);
                    }}
                    required
                    className="form-select"
                  >
                    <option value="credit">Paiement par crédit</option>
                    <option value="comptant">Paiement comptant</option>
                  </select>
                  <small>
                    {demandeForm.modePaiement === 'credit'
                      ? 'Le crédit nécessite un formulaire Prosol électrique'
                      : 'Le paiement comptant est hors programme Prosol électrique'}
                  </small>
                </div>

                {calculResult && (
                  <div className="calcul-result" style={{
                    background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                    padding: '20px',
                    borderRadius: '12px',
                    marginTop: '20px',
                    border: '2px solid #0ea5e9'
                  }}>
                    <h4 style={{color: '#1e3a5f', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px'}}>
                      📊 Estimation de votre installation
                    </h4>
                    <div style={{display: 'grid', gap: '12px'}}>
                      <div style={{display: 'flex', justifyContent: 'space-between', padding: '10px', background: 'white', borderRadius: '6px'}}>
                        <span style={{fontWeight: '500'}}>Consommation annuelle:</span>
                        <span style={{fontWeight: '700', color: '#0ea5e9'}}>{calculResult.consommationAnnuelle} kWh/an</span>
                      </div>
                      <div style={{display: 'flex', justifyContent: 'space-between', padding: '10px', background: 'white', borderRadius: '6px'}}>
                        <span style={{fontWeight: '500'}}>Puissance calculée:</span>
                        <span style={{fontWeight: '700', color: '#0ea5e9'}}>{calculResult.puissanceNecessaire} kWc</span>
                      </div>
                      {!calculResult.isIndustrielle && (
                        <>
                          <div style={{display: 'flex', justifyContent: 'space-between', padding: '10px', background: '#10b981', borderRadius: '6px', color: 'white'}}>
                            <span style={{fontWeight: '600'}}>⚡ Puissance recommandée:</span>
                            <span style={{fontWeight: '700', fontSize: '1.1rem'}}>{calculResult.puissanceCommerciale} kWc</span>
                          </div>
                          <div style={{display: 'flex', justifyContent: 'space-between', padding: '10px', background: 'white', borderRadius: '6px'}}>
                            <span style={{fontWeight: '500'}}>Type de contrat:</span>
                            <span style={{fontWeight: '600', fontSize: '0.9rem'}}>{calculResult.typeContrat}</span>
                          </div>
                          {calculResult.modePaiement === 'credit' && (
                            <>
                              <div style={{display: 'flex', justifyContent: 'space-between', padding: '10px', background: 'white', borderRadius: '6px'}}>
                                <span style={{fontWeight: '500'}}>Formulaire Prosol requis:</span>
                                <span style={{fontWeight: '700', color: '#f59e0b'}}>{calculResult.typeFormulaire}</span>
                              </div>
                              <div style={{display: 'flex', justifyContent: 'space-between', padding: '10px', background: '#fef3c7', borderRadius: '6px', border: '2px solid #f59e0b'}}>
                                <span style={{fontWeight: '600', color: '#92400e'}}>💰 Prix du formulaire:</span>
                                <span style={{fontWeight: '700', fontSize: '1.1rem', color: '#92400e'}}>{calculResult.prixFormulaire} DT</span>
                              </div>
                            </>
                          )}
                        </>
                      )}
                    </div>
                    {!calculResult.isIndustrielle && (
                      <div style={{marginTop: '15px', padding: '12px', background: '#fff7ed', borderRadius: '8px', borderLeft: '4px solid #f59e0b'}}>
                        <h5 style={{margin: '0 0 10px 0', color: '#92400e', fontSize: '0.95rem'}}>📋 Documents requis:</h5>
                        <ul style={{margin: '0', paddingLeft: '20px', fontSize: '0.9rem', color: '#78350f'}}>
                          <li>✓ Contrat à signer</li>
                          <li>✓ Signature obligatoire à la <strong>municipalité</strong></li>
                          <li>✓ Dépôt au siège de <strong>SASTRA</strong></li>
                          {calculResult.modePaiement === 'credit' && (
                            <li>✓ Formulaire Prosol électrique <strong>{calculResult.typeFormulaire}</strong></li>
                          )}
                        </ul>
                      </div>
                    )}

                    {calculResult.isIndustrielle && (
                      <div style={{
                        marginTop: '15px',
                        padding: '20px',
                        background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
                        borderRadius: '12px',
                        border: '3px solid #ef4444',
                        boxShadow: '0 4px 12px rgba(239, 68, 68, 0.2)'
                      }}>
                        <h4 style={{
                          margin: '0 0 15px 0',
                          color: '#991b1b',
                          fontSize: '1.1rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px'
                        }}>
                          <span style={{fontSize: '1.5rem'}}>⚠️</span>
                          Installation Industrielle
                        </h4>
                        <p style={{
                          color: '#7f1d1d',
                          fontSize: '0.95rem',
                          lineHeight: '1.6',
                          margin: '0 0 15px 0',
                          fontWeight: '600'
                        }}>
                          Votre consommation nécessite une installation de plus de 4 kWc. Ce type d'installation est destiné aux entreprises.
                        </p>
                        <div style={{
                          background: 'white',
                          padding: '15px',
                          borderRadius: '8px',
                          marginBottom: '15px'
                        }}>
                          <h5 style={{margin: '0 0 12px 0', color: '#991b1b', fontSize: '0.95rem'}}>
                            📞 Veuillez contacter notre service client :
                          </h5>
                          <div style={{fontSize: '0.9rem', color: '#1e3a5f', lineHeight: '1.8'}}>
                            <p style={{margin: '5px 0', display: 'flex', alignItems: 'center', gap: '8px'}}>
                              <strong>📍 Adresse :</strong> Résidence MAAROUFI, AV Habib Bourguiba, Bardo 2000
                            </p>
                            <p style={{margin: '5px 0', display: 'flex', alignItems: 'center', gap: '8px'}}>
                              <strong>📞 Téléphone :</strong> +216 36 437 473 / +216 93 454 400
                            </p>
                            <p style={{margin: '5px 0', display: 'flex', alignItems: 'center', gap: '8px'}}>
                              <strong>📧 Email :</strong> info@sastraenergy.com
                            </p>
                          </div>
                        </div>
                        <div style={{
                          background: '#fef3c7',
                          padding: '12px',
                          borderRadius: '6px',
                          borderLeft: '4px solid #f59e0b'
                        }}>
                          <p style={{
                            margin: '0 0 10px 0',
                            fontSize: '0.9rem',
                            color: '#92400e',
                            fontWeight: '600'
                          }}>
                            💡 Vous pouvez également trouver le bureau le plus proche de vous :
                          </p>
                          <button
                            type="button"
                            onClick={() => onNavigate && onNavigate('bureaux')}
                            style={{
                              background: 'linear-gradient(135deg, #1e3a5f 0%, #3b82f6 100%)',
                              color: 'white',
                              border: 'none',
                              padding: '10px 20px',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontWeight: '700',
                              fontSize: '0.9rem',
                              width: '100%',
                              boxShadow: '0 2px 8px rgba(30, 58, 95, 0.3)',
                              transition: 'all 0.3s ease'
                            }}
                            onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                            onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                          >
                            📍 Consulter Nos Bureaux
                          </button>
                        </div>
                        <div style={{
                          marginTop: '15px',
                          padding: '12px',
                          background: '#dbeafe',
                          borderRadius: '6px',
                          borderLeft: '4px solid #3b82f6'
                        }}>
                          <p style={{
                            margin: '0',
                            fontSize: '1.05rem',
                            color: '#1e40af',
                            fontWeight: '600',
                            lineHeight: '1.5'
                          }}>
                            🏢 Si vous représentez une entreprise, vous pouvez créer un compte entreprise pour bénéficier de services adaptés.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {!calculResult?.isIndustrielle && (
                  <>
                    <div className="form-group checkbox-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={demandeForm.pompage}
                          onChange={(e) => setDemandeForm({...demandeForm, pompage: e.target.checked})}
                        />
                        <span>Avez-vous un système de pompage ?</span>
                      </label>
                    </div>

                    {demandeForm.pompage && (
                      <div className="form-group">
                        <label>Consommation du pompage (kWh) *</label>
                        <input
                          type="number"
                          value={demandeForm.consommationPompage}
                          onChange={(e) => setDemandeForm({...demandeForm, consommationPompage: e.target.value})}
                          required
                          min="0"
                          step="0.01"
                          placeholder="Ex: 150"
                        />
                      </div>
                    )}
                  </>
                )}
              </div>

              {!calculResult?.isIndustrielle && (
                <div className="form-section" style={{marginTop: '25px'}}>
                  <h4 style={{marginBottom: '20px', color: '#1e3a5f'}}>💭 Votre projet</h4>

                  <div className="form-group">
                    <label>Que pensez-vous du photovoltaïque ? *</label>
                    <textarea
                      value={demandeForm.avisPhotovoltaique}
                      onChange={(e) => setDemandeForm({...demandeForm, avisPhotovoltaique: e.target.value})}
                      required
                      maxLength="1000"
                      rows="4"
                      placeholder="Partagez votre opinion sur l'énergie solaire..."
                    />
                    <small>{demandeForm.avisPhotovoltaique.length}/1000 caractères</small>
                  </div>

                  <div className="form-group">
                    <label>Quelle est votre perspective/objectif ? *</label>
                    <textarea
                      value={demandeForm.perspective}
                      onChange={(e) => setDemandeForm({...demandeForm, perspective: e.target.value})}
                      required
                      maxLength="1000"
                      rows="4"
                      placeholder="Décrivez vos objectifs (économies, autonomie, écologie...)..."
                    />
                    <small>{demandeForm.perspective.length}/1000 caractères</small>
                  </div>
                </div>
              )}

              <div className="modal-actions">
                <button type="button" className="cancel-btn" onClick={() => setShowDemandeForm(false)}>
                  Annuler
                </button>
                {!calculResult?.isIndustrielle && (
                  <button type="submit" className="save-btn">
                    Envoyer la demande
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Nouvelle Réclamation */}
      {showReclamationForm && (
        <div className="modal-overlay" onClick={() => setShowReclamationForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Nouvelle Réclamation</h3>
              <button className="close-btn" onClick={() => setShowReclamationForm(false)}>×</button>
            </div>
            <form onSubmit={handleSubmitReclamation} className="edit-form">
              <div className="form-group">
                <label>Sujet *</label>
                <input
                  type="text"
                  value={reclamationForm.sujet}
                  onChange={(e) => setReclamationForm({...reclamationForm, sujet: e.target.value})}
                  required
                  maxLength="200"
                  placeholder="Ex: Problème avec l'installation"
                />
              </div>
              <div className="form-group">
                <label>Message *</label>
                <textarea
                  value={reclamationForm.message}
                  onChange={(e) => setReclamationForm({...reclamationForm, message: e.target.value})}
                  required
                  maxLength="2000"
                  rows="6"
                  placeholder="Décrivez votre réclamation en détail..."
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="cancel-btn" onClick={() => setShowReclamationForm(false)}>
                  Annuler
                </button>
                <button type="submit" className="save-btn">
                  Envoyer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Détail Devis & Upload Contrats */}
      {showDetailDevis && selectedDemande && (
        <div className="modal-overlay" onClick={() => setShowDetailDevis(false)}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📋 Détail de la Demande de Devis</h3>
              <button className="close-btn" onClick={() => setShowDetailDevis(false)}>×</button>
            </div>

            <div className="detail-devis-content">
              {/* Informations générales */}
              <div className="devis-section">
                <h4>📊 Estimation de votre installation</h4>
                <div className="devis-info-grid">
                  <div className="info-item">
                    <span className="label">Consommation annuelle</span>
                    <span className="value">{selectedDemande.consommationAnnuelle} kWh/an</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Puissance calculée</span>
                    <span className="value">{selectedDemande.puissanceNecessaire} kWc</span>
                  </div>
                  <div className="info-item highlight">
                    <span className="label">⚡ Puissance recommandée</span>
                    <span className="value">{selectedDemande.puissanceCommerciale} kWc</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Mode de paiement</span>
                    <span className="value">{selectedDemande.modePaiement === 'comptant' ? 'Comptant' : 'Crédit'}</span>
                  </div>
                  {selectedDemande.modePaiement === 'credit' && (
                    <>
                      <div className="info-item">
                        <span className="label">Formulaire Prosol</span>
                        <span className="value">{selectedDemande.typeFormulaireProsol}</span>
                      </div>
                      <div className="info-item highlight">
                        <span className="label">💰 Prix formulaire</span>
                        <span className="value" style={{color: '#2e7d32', fontSize: '1.2rem', fontWeight: 'bold'}}>
                          {selectedDemande.prixFormulaire} DT
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Section Upload Contrats */}
              <div className="devis-section contracts-section">
                <h4>📄 Dépôt des Contrats Signés</h4>

                <div className="contract-info-banner">
                  <div className="banner-icon">⚠️</div>
                  <div className="banner-content">
                    <strong>Nombre de contrats requis : {selectedDemande.nombreContratsRequis}</strong>
                    <p>Vous devez téléverser {selectedDemande.nombreContratsRequis === 2 ? 'les 2 contrats' : 'le contrat'} signés à la municipalité :</p>
                    <ul>
                      {selectedDemande.modePaiement === 'credit' ? (
                        <>
                          <li>1. Contrat de crédit</li>
                          <li>2. Formulaire Prosol électrique {selectedDemande.typeFormulaireProsol}</li>
                        </>
                      ) : (
                        <li>1. Contrat hors programme Prosol</li>
                      )}
                    </ul>
                  </div>
                </div>

                <div className="upload-progress">
                  <div className="progress-header">
                    <span>Contrats déposés : {selectedDemande.contratsClient?.length || 0} / {selectedDemande.nombreContratsRequis}</span>
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{
                          width: `${((selectedDemande.contratsClient?.length || 0) / selectedDemande.nombreContratsRequis) * 100}%`,
                          background: ((selectedDemande.contratsClient?.length || 0) >= selectedDemande.nombreContratsRequis) ? '#4caf50' : '#ff9800'
                        }}
                      ></div>
                    </div>
                  </div>
                </div>

                {/* Liste des contrats déjà uploadés */}
                {selectedDemande.contratsClient && selectedDemande.contratsClient.length > 0 && (
                  <div className="uploaded-contracts">
                    <h5>Contrats déposés :</h5>
                    {selectedDemande.contratsClient.map((contrat, index) => (
                      <div key={index} className="contract-item">
                        <div className="contract-info">
                          <span className="contract-icon">📑</span>
                          <div className="contract-details">
                            <span className="contract-name">{contrat.nomFichier}</span>
                            <span className="contract-date">
                              Déposé le {new Date(contrat.dateUpload).toLocaleDateString('fr-FR')}
                            </span>
                          </div>
                        </div>
                        <button
                          className="btn-delete-contract"
                          onClick={() => handleDeleteContract(contrat._id)}
                          style={{
                            background: '#f56565',
                            color: 'white',
                            border: 'none',
                            padding: '8px 16px',
                            borderRadius: '6px',
                            cursor: 'pointer'
                          }}
                        >
                          🗑️ Supprimer
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Bouton d'upload */}
                {(selectedDemande.contratsClient?.length || 0) < selectedDemande.nombreContratsRequis && (
                  <div className="upload-zone">
                    <div className="upload-instructions">
                      <p style={{marginBottom: '15px', color: '#4a5568', fontSize: '0.95rem'}}>
                        📎 Veuillez téléverser {selectedDemande.nombreContratsRequis === 2 ? 'les 2 contrats signés' : 'le contrat signé'} en format PDF
                      </p>
                    </div>

                    <label className="upload-label" htmlFor="contract-upload">
                      <div className="upload-content">
                        <span className="upload-icon">📤</span>
                        <span className="upload-text">
                          {uploadingContract ? 'Téléversement en cours...' : 'Cliquez pour téléverser un contrat'}
                        </span>
                        <span className="upload-subtext">Format accepté : PDF uniquement (max 10 MB)</span>
                      </div>
                      <input
                        id="contract-upload"
                        type="file"
                        accept="application/pdf"
                        multiple={selectedDemande.nombreContratsRequis > 1}
                        onChange={handleUploadContract}
                        disabled={uploadingContract}
                        style={{display: 'none'}}
                      />
                    </label>

                    <button
                      className="btn-upload-contract"
                      onClick={() => document.getElementById('contract-upload').click()}
                      disabled={uploadingContract}
                      style={{
                        width: '100%',
                        background: uploadingContract ? '#cbd5e0' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        border: 'none',
                        padding: '14px 28px',
                        borderRadius: '8px',
                        cursor: uploadingContract ? 'not-allowed' : 'pointer',
                        fontWeight: '600',
                        fontSize: '1rem',
                        marginTop: '15px',
                        transition: 'all 0.3s ease',
                        boxShadow: uploadingContract ? 'none' : '0 4px 15px rgba(102, 126, 234, 0.4)'
                      }}
                    >
                      {uploadingContract ? '⏳ Téléversement en cours...' : '📤 Ajouter un contrat signé'}
                    </button>
                  </div>
                )}

                {(selectedDemande.contratsClient?.length || 0) >= selectedDemande.nombreContratsRequis && (
                  <div className="success-message" style={{
                    background: '#c6f6d5',
                    color: '#22543d',
                    padding: '15px',
                    borderRadius: '8px',
                    textAlign: 'center',
                    fontWeight: '600'
                  }}>
                    ✅ Tous les contrats requis ont été déposés avec succès !
                  </div>
                )}
              </div>

              {/* Autres informations */}
              <div className="devis-section">
                <h4>💭 Votre projet</h4>
                <div className="project-details">
                  <div className="detail-item">
                    <strong>Avis sur le photovoltaïque :</strong>
                    <p>{selectedDemande.avisPhotovoltaique}</p>
                  </div>
                  <div className="detail-item">
                    <strong>Perspective :</strong>
                    <p>{selectedDemande.perspective}</p>
                  </div>
                </div>
              </div>

              {selectedDemande.commentaireAdmin && (
                <div className="devis-section admin-section">
                  <h4>💬 Commentaire de l'administrateur</h4>
                  <p>{selectedDemande.commentaireAdmin}</p>
                </div>
              )}
            </div>

            <div className="modal-actions">
              <button
                type="button"
                className="cancel-btn"
                onClick={() => setShowDetailDevis(false)}
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;